import React, { useEffect } from 'react';
import apiEndpoint from '../config/data';
import { getAuthHeader } from '../../utils/tokenService';
import tokenService from '../../utils/tokenService';
import { isTokenExpired } from '../../utils/jwt';
import { useFeatureFlagsFromContext } from '../../launchdarkly';
import { fetchWithAuth } from '../../utils/apiHelpers';
import { getBlid } from '../../utils/cookieHelpers';
import { checkConnection } from '../widgets/settings/integrationUtils';
import {
  trackIntegrationConnected,
  addUserProperties,
} from '../../utils/analytics';

const CallbackHandler = () => {
  const flags = useFeatureFlagsFromContext();
  const showReskin = flags.showReskin || false;
  const oktaSsoAuthEnabled = flags?.oktaSsoAuth || false;

  /**
   * Helper function to check if user is authenticated via Okta
   * Returns true if PA_BLID cookie exists (set by Okta SSO, readable - not HttpOnly)
   * PA_BLID is only set during SSO flow
   */
  const isOktaAuthenticated = () => getBlid();

  /**
   * Check if this is the first integration by fetching the integration list
   */
  const checkIsFirstIntegration = async () => {
    try {
      const response = await fetchWithAuth('/api/auth_update/', {
        method: 'GET',
      });

      if (!response.ok) {
        console.warn(
          'Failed to fetch integrations, assuming not first integration'
        );
        return false;
      }

      const data = await response.json();
      const integrations = data.integration || [];

      // Check if any integration is already connected using checkConnection utility
      // which checks refresh_token_expired status
      const connectedIntegrations = integrations.filter(app =>
        checkConnection(app)
      );

      return connectedIntegrations.length === 0;
    } catch (error) {
      console.error('Error checking if first integration:', error);
      return false; // Fail safe - don't include blid if we can't determine
    }
  };

  const updateTokens = async () => {
    const queryParameters = new URLSearchParams(window.location.search);
    const code = queryParameters.get('code');
    const realmId = queryParameters.get('realmId');

    // Validate required parameters
    if (!code || !realmId) {
      console.error('Missing required parameters: code or realmId');
      alert('Missing authorization parameters. Please try connecting again.');
      window.open('/', '_self');
      return;
    }

    const integrationId = localStorage.getItem('integration_id');
    const accessToken = localStorage.getItem('access');
    const oktaAuthenticated = isOktaAuthenticated();

    // Check if user is authenticated via access token OR Okta SSO
    if (!accessToken && !oktaAuthenticated) {
      console.error('No access token found and not authenticated via Okta');
      alert('Session expired. Please log in again.');
      window.open('/', '_self');
      return;
    }

    // Check if token is expired and handle accordingly (only for JWT tokens, not Okta SSO)
    if (accessToken && isTokenExpired(accessToken)) {
      console.error('Access token has expired');
      tokenService.handleSessionExpired();
      return;
    }

    // Try to refresh token if needed before making the API call (only for JWT tokens, not Okta SSO)
    if (accessToken) {
      try {
        await tokenService.checkAndRefreshToken();
      } catch (error) {
        console.error('Failed to refresh token:', error);
        // Continue with the existing token - the API call will handle auth errors
      }
    }

    var formdata = new FormData();
    formdata.append('code', code);
    formdata.append('realmId', realmId);
    formdata.append('integration_id', integrationId);
    formdata.append('callback_url', `${window.location.origin}/callback`);

    // Check if this is the first integration (needed for analytics tracking)
    const isFirst = await checkIsFirstIntegration();

    // Only send BLID if oktaSsoAuth is enabled
    // This feature is SSO-specific and avoids unnecessary API calls for non-SSO users
    if (oktaSsoAuthEnabled) {
      // If this is the first integration, add blid to the request
      // Backend will automatically update status to FullyIntegrated
      if (isFirst) {
        const blid = getBlid();
        if (blid) {
          formdata.append('blid', blid);
        } else {
          console.warn('First integration but no BLID found in cookies');
        }
      }
    }

    try {
      const authHeader = getAuthHeader();
      const response = await fetch(apiEndpoint + '/api/auth/', {
        method: 'POST',
        credentials: 'include',
        headers: authHeader ? { Authorization: authHeader } : {},
        body: formdata,
      });

      // Check if response is ok
      if (!response.ok) {
        console.error('HTTP error:', response.status, response.statusText);

        // Try to get error details
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          console.error('Error data:', errorData);
          alert(
            `Authentication failed: ${errorData.message || 'Unknown error'}`
          );
        } else {
          // If it's HTML, log the first part for debugging
          const errorText = await response.text();
          console.error('Non-JSON response:', errorText.substring(0, 500));

          if (response.status === 401) {
            alert('Session expired. Please log in again.');
          } else if (response.status === 403) {
            alert('Access denied. Please check your permissions.');
          } else {
            alert(`Server error (${response.status}). Please try again later.`);
          }
        }
        window.open('/', '_self');
        return;
      }

      // Check content type before parsing JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text();
        console.error(
          'Expected JSON but got:',
          contentType,
          responseText.substring(0, 500)
        );
        alert('Unexpected response format from server. Please try again.');
        window.open('/', '_self');
        return;
      }

      const data = await response.json();

      if (data.code === undefined) {
        // Integration successfully connected
        // If blid was included, backend automatically updated status to FullyIntegrated

        // Track integration connected event
        try {
          const integrationType =
            localStorage.getItem('integration_type') || '';
          const appName = localStorage.getItem('integration_app_name') || '';
          const integrationDesc =
            localStorage.getItem('integration_desc') || '';

          trackIntegrationConnected({
            integrationId: integrationId || '',
            integrationType: integrationType,
            appName: appName || integrationDesc,
            isFirstIntegration: isFirst,
          });

          // Update user property to indicate they have a connected integration
          addUserProperties({
            integrationConnected: true,
          });

          // Clean up localStorage after tracking
          localStorage.removeItem('integration_type');
          localStorage.removeItem('integration_app_name');
          localStorage.removeItem('integration_desc');
        } catch (error) {
          console.error('Error tracking integration connected:', error);
          // Don't block the redirect if tracking fails
        }

        // Check if user needs to connect accounting software
        if (data.needsIntegration) {
          if (showReskin) {
            window.open('/integrations', '_self');
          } else {
            window.open('/Dash?view=integrationSettings', '_self');
          }
        } else {
          if (showReskin) {
            window.open('/overview', '_self');
          } else {
            window.open('/Dash', '_self');
          }
        }
      } else {
        window.open('/', '_self');
        alert('Session Expired!.');
      }
    } catch (err) {
      console.error('Error during OAuth callback:', err);

      // Redirect on error based on feature flag
      if (showReskin) {
        window.open('/overview', '_self');
      } else {
        window.open('/Dash', '_self');
      }
    }
  };

  useEffect(() => {
    // Wrap async function in IIFE to properly handle async operations in useEffect
    (async () => {
      try {
        await updateTokens();
      } catch (error) {
        console.error('Error in updateTokens:', error);
      }
    })();
  }, []);

  return <h7>Loading!!</h7>;
};

export default CallbackHandler;
